import { H2 } from "components/ui/typography/titles/Titles"
import { Input, Form } from "antd"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useEffect, useState } from "react"
import { ValidatePassword } from "components/ui/validatePassword/validatePassword"
import { AuthProvider } from "components/providers/Forms.provider"
import { useRegistrationStore } from "store/auth.store"
import { useRegistration } from "hooks/api/auth/auth.hook"
import { useRegistrationStepsStore } from "store/auth.store"
import { useValidatePassword } from "hooks/validatePassword.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { ButtonGetBack } from "components/ui/buttons/ButtonGetBack/ButtonGetBack"
import style from "./UserPassword.module.scss"

const { title } = style

type Props = { token: string | undefined | null }

export const UserPassword = ({ token }: Props) => {
	const [submittable, setSubmittable] = useState(false)

	const { mutate: registrationMutate } = useRegistration()
	const { setRegistration, data } = useRegistrationStore()
	const { setStep } = useRegistrationStepsStore()
	const { formatError } = useApiErrorFormatter()

	const [form] = Form.useForm()
	const values = Form.useWatch([], form)
	const validatedPassword = useValidatePassword(values)

	useEffect(() => {
		const allKeysAreValid = Object.values(validatedPassword).every((val) => val)
		const passwordMatch = values?.password === values?.confirmPassword

		setRegistration({
			password: values?.password,
			confirmPassword: values?.confirmPassword,
			token: token,
		})

		setSubmittable(passwordMatch && allKeysAreValid)
	}, [validatedPassword, values?.password, values?.confirmPassword, setRegistration, token])

	useEffect(() => {
		form.validateFields({ validateOnly: true }).then(
			() => {
				const password = values?.password
				const confirmPassword = values?.confirmPassword
				const passwordMatch = password === confirmPassword

				setSubmittable(passwordMatch)
			},
			() => {
				setSubmittable(false)
			},
		)
	}, [values, form])

	const onSubmitHandle = () => {
		setSubmittable(false)
		registrationMutate(data, {
			onSuccess: () => {
				setStep(3)
			},
			onError: (e) => {
				setSubmittable(true)
				formatError(e)
			},
		})
	}

	return (
		<>
			<ButtonGetBack onClick={() => setStep(1)}>Back</ButtonGetBack>
			<H2 className={title}>Create a password</H2>
			<AuthProvider>
				<Form
					form={form}
					name="formPassword"
					size="large"
					initialValues={{
						password: data?.password || "",
						confirmPassword: data?.confirmPassword || "",
					}}
				>
					<Form.Item name="password" rules={[{ required: true }]}>
						<Input.Password placeholder="Password" />
					</Form.Item>
					<Form.Item
						name="confirmPassword"
						rules={[
							{
								required: true,
								message: "Please confirm your password",
							},
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value || getFieldValue("password") === value) {
										return Promise.resolve()
									}
									return Promise.reject(
										new Error("Passwords do not match. Please enter the same password in both fields"),
									)
								},
							}),
						]}
					>
						<Input.Password placeholder="Confirm Password" />
					</Form.Item>
					<ValidatePassword validate={validatedPassword} />
					<ButtonPrimary onClick={onSubmitHandle} submittable={submittable}>
						Continue
					</ButtonPrimary>
				</Form>
			</AuthProvider>
		</>
	)
}
